import type { LoaderFunctionArgs } from "@remix-run/node";
import { Outlet, useLoaderData, useLocation } from "@remix-run/react";

import Footer from "~/components/landing-page/Footer/Footer";
import Header from "~/components/landing-page/Header/Header";
import { mapNavigation } from "~/components/landing-page/Sections/util/map-navigation.server";
import { mapFooter } from "~/components/landing-page/Sections/util/mapFooter.server";
import type { Footer as FooterType } from "~/generated/schema.server";
import { hygraphSdk } from "~/integrations/hygraph/client";
import { isUnauthenticated } from "~/modules/auth";
import { SupaStripeStackError } from "~/utils/error";

export const loader = async ({ request }: LoaderFunctionArgs) => {
  try {
    const [{ GetNavItems, GetFooter }, userIsUnauthenticated] =
      await Promise.all([hygraphSdk(), isUnauthenticated(request)]);

    const [{ page }, { footers }] = await Promise.all([
      GetNavItems(),
      GetFooter(),
    ]);

    if (!page) {
      throw new SupaStripeStackError({
        message: "No page found",
      });
    }

    return {
      footer: mapFooter(footers[0] as FooterType),
      navItems: mapNavigation(page.sections),
      isUnauthenticated: userIsUnauthenticated,
    };
  } catch (cause) {
    throw new SupaStripeStackError({
      cause,
      message: "Failed to load pages",
      tag: "Pages loader",
    });
  }
};

export type PagesLoaderType = typeof loader;

const Pages = () => {
  const { navItems, isUnauthenticated, footer } =
    useLoaderData<PagesLoaderType>();
  const location = useLocation();
  const isHome = location.pathname === "/";
  const isLegalPage =
    location.pathname === "/privacy-policy" ||
    location.pathname === "/terms-and-conditions";

  return (
    <div className="relative bg-offWhite">
      <div className="relative z-[1]">
        <Header
          navItems={navItems}
          showAppButton={!isUnauthenticated}
          isHome={isHome}
          showBanner={!isLegalPage}
        />
        <Outlet />
        <Footer isHome={isHome} {...footer} />
      </div>
    </div>
  );
};

export default Pages;
